import { ClientOnboardingDraft } from '../types/clientOnboarding';
import { IS_AWS_MODE } from '../config/frontend';
import { getRequestAuthToken } from '../auth/appliedAuthToken';

const BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

function authHeaders(): Record<string, string> {
  // ONE guarded token read per request attempt. In AWS mode a missing guarded
  // tab-applied token throws locally BEFORE any network I/O (fail-closed), so a
  // cross-tab transition between the guard and the fetch can never send a
  // request without Authorization.
  const token = getRequestAuthToken();
  if (IS_AWS_MODE && !token) {
    throw new ClientOnboardingApiError('unauthenticated', 'Authentication required. Please log in again.', 401);
  }
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// ---------------------------------------------------------------------------
// Plan response types — mirror the backend POST /api/v1/admin/client-onboarding/plan
// ---------------------------------------------------------------------------

export interface PlanOperation {
  sequence: number;
  operation_id: string;
  type: string;
  phase: string;
  target_type: string | null;
  target_id: string | null;
  description_code: string | null;
  preconditions: string[];
  write_enabled: boolean;
}

export interface PlanBlocker {
  code: string;
  severity: string;
  path: string | null;
  message_key: string | null;
  metadata?: Record<string, unknown>;
}

export interface PlanSafety {
  commands_enabled: boolean;
  downlink_enabled: boolean;
  commissioned: boolean;
  ttn_mutation_planned: boolean;
  hardware_binding_planned: boolean;
}

export interface PlanData {
  schema_version: number;
  plan_version: number;
  plan_id: string;
  draft_digest: string;
  plan_digest: string;
  status: 'READY' | 'BLOCKED';
  proposed: Record<string, unknown>;
  operations: PlanOperation[];
  collisions: PlanBlocker[];
  warnings: PlanBlocker[];
  safety: PlanSafety;
}

export interface PlanApiResponse {
  success: boolean;
  data: PlanData | null;
  error: string | null;
  error_code?: string | null;
  request_id?: string | null;
}

// ---------------------------------------------------------------------------
// Semantic frontend error types — never expose raw backend/network strings
// ---------------------------------------------------------------------------

export type ClientOnboardingApiErrorCode =
  | 'network_error'
  | 'unauthenticated'
  | 'forbidden'
  | 'validation_error'
  | 'dependency_unavailable'
  | 'server_error'
  | 'unknown_error';

export class ClientOnboardingApiError extends Error {
  public readonly code: ClientOnboardingApiErrorCode;
  public readonly statusCode: number | null;
  public readonly diagnostic: string;  // safe machine code for diagnostics, NEVER rendered

  constructor(
    code: ClientOnboardingApiErrorCode,
    message: string,
    statusCode: number | null = null,
    diagnostic: string = '',
  ) {
    super(message);
    this.name = 'ClientOnboardingApiError';
    this.code = code;
    this.statusCode = statusCode;
    this.diagnostic = diagnostic;
  }
}

/** Map an HTTP status + optional error body to a semantic frontend error. */
function classifyError(status: number, body?: any): ClientOnboardingApiError {
  const raw = (body && (body.error || body.message || '')) || '';
  const errorCode = (body && body.error_code) || '';

  switch (status) {
    case 401:
      return new ClientOnboardingApiError('unauthenticated', 'Authentication required. Please log in again.', status, raw);
    case 403:
      return new ClientOnboardingApiError('forbidden', 'You do not have permission to perform this action.', status, raw);
    case 422:
      return new ClientOnboardingApiError('validation_error', 'The submitted data is invalid.', status, raw);
    case 503:
      return new ClientOnboardingApiError('dependency_unavailable', 'Service temporarily unavailable. Please try again later.', status, errorCode || raw);
    default:
      if (status >= 500) {
        return new ClientOnboardingApiError('server_error', 'An internal server error occurred.', status, raw);
      }
      return new ClientOnboardingApiError('unknown_error', 'An unexpected error occurred.', status, raw);
  }
}

/**
 * Submit the client onboarding draft to the server-side plan endpoint.
 *
 * Sends the draft wrapped in the versioned envelope expected by the backend.
 * The endpoint performs a pure analysis — no data is written.
 *
 * @throws {ClientOnboardingApiError} with a semantic code for safe UI rendering.
 */
export async function generatePlan(draft: ClientOnboardingDraft): Promise<PlanData> {
  // Build the guarded headers ONCE for this request attempt. In AWS mode this
  // throws locally (unauthenticated, 401) when no guarded tab-applied token
  // exists — BEFORE fetch — so a cross-tab transition cannot send the request
  // without Authorization. The local guard is never remapped to network_error.
  const headers = authHeaders();

  const url = `${BASE}/api/v1/admin/client-onboarding/plan`;

  const body = {
    schema_version: 1,
    mode: 'PLAN_ONLY',
    draft,
  };

  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
  } catch (err: unknown) {
    const diagnostic = err instanceof Error ? err.message : String(err);
    throw new ClientOnboardingApiError('network_error', 'Unable to reach the server. Please check your connection.', null, diagnostic);
  }

  if (!res.ok) {
    let body: any = null;
    try { body = await res.json(); } catch { /* ignore */ }
    throw classifyError(res.status, body);
  }

  const envelope: PlanApiResponse = await res.json();
  if (!envelope.success || !envelope.data) {
    throw new ClientOnboardingApiError('server_error', 'The server returned an unexpected response.', res.status, envelope.error || '');
  }
  return envelope.data;
}
